/**
 * Add rows to a bench: pick a question, look at its real submissions, tick the
 * ones worth benching.
 *
 * Hand-picked only — nothing here ranks or recommends. Selection is editorial
 * (design §4.1), and a miner that guessed which solutions are interesting would
 * quietly decide what the bench can find.
 *
 * Lives beside the Questions tab because adding to a bench IS adding a
 * question: the solutions are chosen within one, and `initialQuestionId` lets a
 * question already on the bench be extended without retyping its id.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QuestionSearchFilter } from '@shared-ui/question-picker';
import { adminApi } from '../../api/adminApi';
import type { CandidateSubmission, VariantQuality } from '../../types/scenarioEvals';
import { READING_DIFFICULTY_TAGS, ASSESSING_DIFFICULTY_TAGS } from '../../types/scenarioEvals';
import './BenchTab.css';

export function AddRowsModal({ datasetId, initialQuestionId, onClose, onAdded }: {
    datasetId: string;
    /** Prefilled when extending a question the bench already holds. */
    initialQuestionId?: string;
    onClose: () => void;
    onAdded: () => void;
}) {
    // One question id, not a draft and a committed copy: choosing from the
    // search picker IS the commit, so there is nothing left for a button to do.
    const [questionId, setQuestionId] = useState(initialQuestionId ?? '');
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [variantQuality, setVariantQuality] = useState<VariantQuality | ''>('');
    const [tags, setTags] = useState<Set<string>>(new Set());
    const [note, setNote] = useState('');

    const { data, isFetching, error } = useQuery({
        queryKey: ['benchCandidates', datasetId, questionId],
        queryFn: () => adminApi.listBenchCandidates(datasetId, questionId),
        enabled: !!questionId,
    });

    const add = useMutation({
        mutationFn: () => adminApi.captureScenarioInputs(datasetId, {
            inputIds: [...picked],
            ...(variantQuality ? { variantQuality } : {}),
            ...(tags.size ? { difficultyTags: [...tags] } : {}),
            ...(note ? { curationNote: note } : {}),
        }),
        onSuccess: onAdded,
    });

    const toggle = (set: Set<string>, key: string, apply: (s: Set<string>) => void) => {
        const next = new Set(set);
        next.has(key) ? next.delete(key) : next.add(key);
        apply(next);
    };

    const candidates = data?.candidates ?? [];

    return (
        <div className="bench-modal-backdrop" onClick={onClose}>
            <div className="bench-modal wide" onClick={e => e.stopPropagation()}>
                <h2>Add a question to the bench</h2>
                <p className="bench-muted">
                    Find the question you want to bench. Every real student submission for it is listed —
                    pick the solution variants worth judging.
                </p>

                <div className="bench-search">
                    <QuestionSearchFilter
                        includeOwned
                        value={questionId}
                        onChange={qid => { setQuestionId(qid); setPicked(new Set()); }}
                        placeholder="Search question…"
                    />
                </div>

                {isFetching && <p className="bench-muted">Looking up its submissions…</p>}
                {error && <p className="bench-error">{String(error)}</p>}
                {questionId && !isFetching && !candidates.length && (
                    <p className="bench-muted">No production submissions found for that question.</p>
                )}

                {!!candidates.length && (
                    <div className="bench-candidates">
                        {candidates.map(c => (
                            <CandidateCard
                                key={c.modelInputId}
                                c={c}
                                checked={picked.has(c.modelInputId)}
                                onToggle={() => toggle(picked, c.modelInputId, setPicked)}
                            />
                        ))}
                    </div>
                )}

                {!!picked.size && (
                    <div className="bench-curation">
                        <h4>Applies to all {picked.size} selected</h4>
                        <label>
                            Variant
                            <select value={variantQuality} onChange={e => setVariantQuality(e.target.value as VariantQuality | '')}>
                                <option value="">— unset —</option>
                                <option value="correct">correct</option>
                                <option value="partially_correct">right answer, wrong method</option>
                                <option value="common_mistake">common mistake</option>
                            </select>
                        </label>

                        <div className="bench-tagpick">
                            <span className="bench-taggroup-label">Hard to read</span>
                            {READING_DIFFICULTY_TAGS.map(t => (
                                <TagToggle key={t} tag={t} on={tags.has(t)} onClick={() => toggle(tags, t, setTags)} />
                            ))}
                        </div>
                        <div className="bench-tagpick">
                            <span className="bench-taggroup-label">Hard to assess</span>
                            {ASSESSING_DIFFICULTY_TAGS.map(t => (
                                <TagToggle key={t} tag={t} on={tags.has(t)} onClick={() => toggle(tags, t, setTags)} />
                            ))}
                        </div>

                        <label className="bench-note">
                            Why this row
                            <input type="text" value={note} onChange={e => setNote(e.target.value)}
                                placeholder="optional — what makes it worth benching" />
                        </label>
                    </div>
                )}

                {add.error && <p className="bench-error">{String(add.error)}</p>}

                <div className="bench-modal-actions">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" disabled={!picked.size || add.isPending} onClick={() => add.mutate()}>
                        {add.isPending ? 'Adding…' : `Add ${picked.size || ''} row${picked.size === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CandidateCard({ c, checked, onToggle }: { c: CandidateSubmission; checked: boolean; onToggle: () => void }) {
    return (
        <label className={`bench-candidate${c.alreadyAdded ? ' added' : ''}`}>
            <input type="checkbox" checked={checked} disabled={c.alreadyAdded} onChange={onToggle} />
            {c.imageUrl
                ? <img src={c.imageUrl} alt="student work" loading="lazy" />
                : <div className="bench-candidate-text">{c.textAnswer?.slice(0, 160) || <em>no answer</em>}</div>}
            <div className="bench-candidate-meta">
                {c.alreadyAdded && <span className="bench-pill added">already on bench</span>}
                <span className="bench-pill">{c.hasImageAnswer ? 'handwritten' : 'typed'}</span>
                {c.verdict && <span className={`bench-pill verdict-${c.verdict}`}>{c.verdict}</span>}
                {c.score !== undefined && <span className="bench-pill">score {c.score}</span>}
                {c.stepCount !== undefined && <span className="bench-pill">{c.stepCount} steps</span>}
                <span className="bench-pill">{c.outputCount} output{c.outputCount === 1 ? '' : 's'}</span>
                {c.createdAt && <span className="bench-muted">{c.createdAt.slice(0, 10)}</span>}
            </div>
        </label>
    );
}

function TagToggle({ tag, on, onClick }: { tag: string; on: boolean; onClick: () => void }) {
    return (
        <button type="button" className={`bench-tagbtn${on ? ' on' : ''}`} onClick={onClick}>{tag}</button>
    );
}
